import type { InventoryManifest } from '../api/goods';
import type { InventoryOutboxScope } from './inventoryOfflineOutbox';

const KEY = 'coop.inventory.offline-context.v1';
const PREPARED_KEY = 'coop.inventory.offline-prepared.v1';
const TOKEN_KEY = 'coop_token';
const MAX_AGE_MS = 60 * 60 * 1000;
let preparationGeneration = 0;
let registrationPromise: Promise<void> | null = null;

export type InventoryOfflineContext = InventoryOutboxScope & {
  authBinding: string;
  confirmedAt: string;
  expiresAt: number;
  warehouseName: string;
};

export function cancelInventoryOfflinePreparation() {
  preparationGeneration += 1;
  try { sessionStorage.removeItem(KEY); } catch { /* unavailable storage */ }
}
export function clearInventoryOfflineContext(scope?: Pick<InventoryOutboxScope, 'userId' | 'merchantId'> & { warehouseId?: string }) {
  preparationGeneration += 1;
  try {
    if (!scope) { sessionStorage.removeItem(KEY); sessionStorage.removeItem(PREPARED_KEY); return; }
    const matches = (value: InventoryOfflineContext) => value.userId === scope.userId && value.merchantId === scope.merchantId && (scope.warehouseId === undefined || value.warehouseId === scope.warehouseId);
    const current = JSON.parse(sessionStorage.getItem(KEY) ?? 'null');
    if (current && matches(current)) sessionStorage.removeItem(KEY);
    sessionStorage.setItem(PREPARED_KEY, JSON.stringify(storedContexts().filter(value => !matches(value))));
  } catch {
    try { sessionStorage.removeItem(KEY); sessionStorage.removeItem(PREPARED_KEY); } catch { /* authentication must work with blocked storage */ }
  }
}
function storedContexts(): InventoryOfflineContext[] {
  const value = JSON.parse(sessionStorage.getItem(PREPARED_KEY) ?? '[]');
  return Array.isArray(value) ? value : [];
}

function tokenIdentity(token: string): { sub: string; exp: number } | null {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const value = JSON.parse(atob(payload));
    return typeof value.sub === 'string' && Number.isFinite(value.exp) ? value : null;
  } catch { return null; }
}

async function binding(token: string) {
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  return [...new Uint8Array(hash)].map(value => value.toString(16).padStart(2, '0')).join('');
}

export async function ensureInventoryOfflineShell() {
  if (!registrationPromise) {
    registrationPromise = (async () => {
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('Offline inventory installation timed out')), 10_000);
        void navigator.serviceWorker.register('/inventory-offline-sw.js', { scope: '/', updateViaCache: 'none' })
          .then(async registration => {
            const pending = registration.installing ?? registration.waiting;
            if (pending && pending.state !== 'activated') await new Promise<void>((done, fail) => {
              const updateTimer = setTimeout(() => {
                pending.removeEventListener('statechange', changed);
                fail(new Error('Offline shell update timed out'));
              }, 10_000);
              const changed = () => {
                if (pending.state === 'activated') { clearTimeout(updateTimer); pending.removeEventListener('statechange', changed); done(); }
                else if (pending.state === 'redundant') { clearTimeout(updateTimer); pending.removeEventListener('statechange', changed); fail(new Error('Offline shell update failed')); }
              };
              pending.addEventListener('statechange', changed); changed();
            });
            return navigator.serviceWorker.ready;
          })
          .then(() => { clearTimeout(timer); resolve(); }, error => { clearTimeout(timer); reject(error); });
      });
      if (!navigator.serviceWorker.controller?.scriptURL.endsWith('/inventory-offline-sw.js')) {
        await new Promise<void>((resolve, reject) => {
          const changed = () => {
            if (!navigator.serviceWorker.controller?.scriptURL.endsWith('/inventory-offline-sw.js')) return;
            clearTimeout(timer);
            navigator.serviceWorker.removeEventListener('controllerchange', changed);
            resolve();
          };
          const timer = setTimeout(() => {
            navigator.serviceWorker.removeEventListener('controllerchange', changed);
            reject(new Error('Offline inventory shell is not ready'));
          }, 10_000);
          navigator.serviceWorker.addEventListener('controllerchange', changed);
          changed();
        });
      }
    })().catch(error => { registrationPromise = null; throw error; });
  }
  await registrationPromise;
  if (!navigator.serviceWorker.controller?.scriptURL.endsWith('/inventory-offline-sw.js')) {
    registrationPromise = null;
    throw new Error('Offline inventory shell lost control');
  }
}

// Called only after an authorized online manifest has been committed to IndexedDB.
export async function prepareInventoryOfflineContext(
  scope: InventoryOutboxScope,
  manifest: InventoryManifest,
  warehouseName: string,
  stillActive: () => boolean,
): Promise<boolean> {
  const generation = ++preparationGeneration;
  try {
    if (!isSecureContext || !('serviceWorker' in navigator) || typeof indexedDB === 'undefined') return false;
    const token = localStorage.getItem(TOKEN_KEY);
    const identity = token ? tokenIdentity(token) : null;
    if (!token || identity?.sub !== scope.userId || identity.exp * 1000 <= Date.now()
      || manifest.session.status !== 'COUNTING' || manifest.merchantId !== scope.merchantId
      || manifest.session.id !== scope.sessionId || manifest.session.warehouseId !== scope.warehouseId) return false;
    await ensureInventoryOfflineShell();
    const authBinding = await binding(token);
    if (generation !== preparationGeneration || !stillActive() || identity.exp * 1000 <= Date.now()
      || localStorage.getItem(TOKEN_KEY) !== token) return false;
    const context: InventoryOfflineContext = {
      ...scope, authBinding, warehouseName, confirmedAt: manifest.confirmedAt,
      expiresAt: Math.min(Date.now() + MAX_AGE_MS, identity.exp * 1000),
    };
    const previous = await listInventoryOfflineContexts();
    if (generation !== preparationGeneration || !stillActive() || localStorage.getItem(TOKEN_KEY) !== token) return false;
    const contexts = previous.filter(value => value.merchantId !== context.merchantId || value.warehouseId !== context.warehouseId || value.sessionId !== context.sessionId);
    sessionStorage.setItem(PREPARED_KEY, JSON.stringify([...contexts, context].slice(-20)));
    sessionStorage.setItem(KEY, JSON.stringify(context));
    return true;
  } catch { return false; }
}

export async function readInventoryOfflineContext(): Promise<InventoryOfflineContext | null> {
  try {
    const context = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as InventoryOfflineContext | null;
    const token = localStorage.getItem(TOKEN_KEY);
    const identity = token ? tokenIdentity(token) : null;
    if (!context || !token || identity?.sub !== context.userId || identity.exp * 1000 <= Date.now()
      || !Number.isFinite(context.expiresAt) || context.expiresAt <= Date.now()
      || ![context.userId, context.merchantId, context.warehouseId, context.sessionId, context.manifestHash].every(value => typeof value === 'string' && value.length > 0)) return null;
    if (await binding(token) !== context.authBinding || localStorage.getItem(TOKEN_KEY) !== token) return null;
    return context;
  } catch { return null; }
}

// Cached manifests alone grant no access: only online-confirmed token-bound leases are selectable.
export async function listInventoryOfflineContexts(): Promise<InventoryOfflineContext[]> {
  try {
    const current = JSON.parse(sessionStorage.getItem(KEY) ?? 'null');
    const candidates = storedContexts();
    if (current && !candidates.some(value => value.sessionId === current.sessionId && value.merchantId === current.merchantId)) candidates.push(current);
    const token = localStorage.getItem(TOKEN_KEY);
    const identity = token ? tokenIdentity(token) : null;
    if (!token || !identity || identity.exp * 1000 <= Date.now()) return [];
    const authBinding = await binding(token);
    if (localStorage.getItem(TOKEN_KEY) !== token) return [];
    return candidates.filter(value => value && value.authBinding === authBinding && value.userId === identity.sub
      && Number.isFinite(value.expiresAt) && value.expiresAt > Date.now()
      && [value.userId, value.merchantId, value.warehouseId, value.sessionId, value.manifestHash].every(field => typeof field === 'string' && field.length > 0));
  } catch { return []; }
}
